import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@projectobelisk/uikit/components/feedback/empty";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Megaphone } from "lucide-react";
import { z } from "zod/v4";
import { CampaignsTable } from "@/components/campaigns/table";
import {
  TABLE_DEFAULTS as CAMPAIGNS_TABLE_DEFAULTS,
  TABLE_KEY as CAMPAIGNS_TABLE_KEY,
} from "@/components/campaigns/table/hooks";
import { CampaignsTableSkeleton } from "@/components/campaigns/table/skeleton";
import { campaignQueries } from "@/queries/campaigns";
import {
  type CampaignStatus,
  campaignStatusSchema,
} from "@/schemas/models/campaign";
import { useTableStore } from "@/stores/tables";

const campaignsSearchSchema = z.object({
  page: z.coerce.number().min(1).catch(1).optional(),
  search: z.string().optional(),
  status: z.enum([...campaignStatusSchema.options, "all"]).optional(),
});

export const Route = createFileRoute("/_app/speed-to-lead/campaigns/")({
  validateSearch: campaignsSearchSchema,
  component: CampaignsPage,
});

function CampaignsPage() {
  const search = Route.useSearch();
  const { sort, size } = useTableStore(
    CAMPAIGNS_TABLE_KEY,
    CAMPAIGNS_TABLE_DEFAULTS,
  );
  const page = search.page ?? 1;
  const status = search.status ?? "active";

  const { data, isFetching, isLoading } = useQuery({
    ...campaignQueries.list({
      limit: size,
      offset: (page - 1) * size,
      search: search.search,
      sort,
      filter:
        status !== "all" ? { status: status as CampaignStatus } : undefined,
    }),
    placeholderData: keepPreviousData,
  });

  if (isLoading) {
    return <CampaignsTableSkeleton />;
  }

  if (data && data.meta.total === 0 && !search.search && status === "all") {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Megaphone />
          </EmptyMedia>
          <EmptyTitle>No campaigns yet</EmptyTitle>
          <EmptyDescription>
            Create your first campaign to reach leads faster.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  if (!data) return null;

  return (
    <CampaignsTable
      data={data.data}
      total={data.meta.total}
      isFetching={isFetching}
    />
  );
}
